// Step 7 Phase 0 — Mobile OOM soft-warning modal.
//
// Shown by main.ts bootstrap BEFORE the splat scene starts loading,
// when shouldWarnMobileSize() returns true (mobile + ply + size > 150MB).
// Returns a Promise<boolean>: true = user accepts the OOM risk and
// orchestrator proceeds, false = user declines (orchestrator dispatches
// LOAD_CANCELLED_BY_USER).
//
// A11y: role=dialog + aria-modal=true + aria-labelledby + aria-describedby.
// Focus trap on Tab/Shift+Tab (cycles between two buttons). ESC handler
// LOCAL to the modal (Codex iter 1 review §D fix): the global ESC chain
// in orchestrator is only installed in `loaded` state, but this modal
// shows in `fetching-meta` so we install our own keydown listener.
//
// Focus restore: capture document.activeElement at open, restore on close.
//
// Caller flow:
//   const proceed = await showMobileSizeWarningModal(uiRoot, { meta, currentUrl });
//   if (!proceed) { return; } // orchestrator already dispatched cancel
//   await orchestrator.start();

import type { ModelMeta } from '../../api/types.js';
import { copyText } from '../../share/clipboard.js';

export interface MobileSizeWarningProps {
  meta: Pick<ModelMeta, 'filename' | 'size_bytes'>;
  shareUrl: string;
}

const ICON_WARNING = `<svg aria-hidden="true" viewBox="0 0 24 24" width="32" height="32"><path d="M12 3 L22 20 H2 Z" stroke="currentColor" stroke-width="2" fill="none" stroke-linejoin="round"/><path d="M12 10 v5 M12 17.5 v0.5" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>`;

function formatMb(bytes: number): string {
  return `${Math.round(bytes / (1024 * 1024))} MB`;
}

/**
 * Renders the mobile OOM soft-warning modal and returns the user's
 * decision. Side-effects: appends to root, captures focus, copies URL
 * to clipboard if user clicks "Copy link". Cleanup is automatic on
 * resolve (modal node removed, listeners detached, focus restored).
 */
export function showMobileSizeWarningModal(
  root: HTMLElement,
  props: MobileSizeWarningProps,
): Promise<boolean> {
  return new Promise<boolean>((resolve) => {
    const opener = document.activeElement as HTMLElement | null;

    const wrap = document.createElement('div');
    wrap.classList.add('viewer-ui-interactive');
    // z-index above any other UI; pre-load this is the only modal so
    // no stack management is required.
    wrap.style.cssText =
      'position:fixed;inset:0;z-index:100;display:flex;align-items:center;justify-content:center;padding:1rem;font-family:system-ui,sans-serif';

    const backdrop = document.createElement('div');
    backdrop.setAttribute('aria-hidden', 'true');
    backdrop.style.cssText =
      'position:absolute;inset:0;background:rgba(0,0,0,0.70);backdrop-filter:blur(6px)';
    wrap.appendChild(backdrop);

    const dialog = document.createElement('div');
    dialog.setAttribute('role', 'dialog');
    dialog.setAttribute('aria-modal', 'true');
    dialog.setAttribute('aria-labelledby', 'oniri-mobile-warn-title');
    dialog.setAttribute('aria-describedby', 'oniri-mobile-warn-body');
    dialog.style.cssText =
      'position:relative;max-width:28rem;width:100%;background:rgba(15,15,17,0.95);backdrop-filter:blur(12px);border:1px solid rgba(255,255,255,0.10);border-radius:1.25rem;padding:1.5rem;color:#f4f4f5;box-shadow:0 20px 50px rgba(0,0,0,0.5)';
    wrap.appendChild(dialog);

    // Header with warning icon + title
    const header = document.createElement('div');
    header.style.cssText = 'display:flex;align-items:center;gap:0.75rem;margin-bottom:0.75rem';
    const iconWrap = document.createElement('span');
    iconWrap.setAttribute('aria-hidden', 'true');
    iconWrap.style.cssText =
      'display:flex;width:2.75rem;height:2.75rem;align-items:center;justify-content:center;border-radius:0.75rem;background:rgba(245,158,11,0.15);color:#f59e0b;flex-shrink:0';
    iconWrap.innerHTML = ICON_WARNING;
    header.appendChild(iconWrap);
    const title = document.createElement('h2');
    title.id = 'oniri-mobile-warn-title';
    title.style.cssText = 'font-size:1.125rem;font-weight:600;margin:0;line-height:1.3';
    title.textContent = 'Modello pesante per dispositivo mobile';
    header.appendChild(title);
    dialog.appendChild(header);

    // Body
    const body = document.createElement('div');
    body.id = 'oniri-mobile-warn-body';
    body.style.cssText = 'font-size:0.875rem;line-height:1.5;color:#d4d4d8;margin-bottom:1.25rem';
    const sizeMb = formatMb(props.meta.size_bytes);
    const filename = props.meta.filename;
    const intro = document.createElement('p');
    intro.style.cssText = 'margin:0 0 0.5rem 0';
    intro.textContent = `${filename} pesa ${sizeMb}.`;
    body.appendChild(intro);
    const reason = document.createElement('p');
    reason.style.cssText = 'margin:0';
    reason.textContent =
      'Sui dispositivi mobili la memoria del browser potrebbe non essere sufficiente e la pagina potrebbe chiudersi. Per la migliore esperienza apri questo link da PC desktop.';
    body.appendChild(reason);
    dialog.appendChild(body);

    // Toast region for "link copied" announcement
    const toast = document.createElement('div');
    toast.setAttribute('role', 'status');
    toast.setAttribute('aria-live', 'polite');
    toast.style.cssText =
      'min-height:1.25rem;font-size:0.75rem;color:#34d399;margin-bottom:0.75rem;opacity:0;transition:opacity 200ms';
    dialog.appendChild(toast);

    // Buttons row
    const buttons = document.createElement('div');
    buttons.style.cssText = 'display:flex;flex-direction:column;gap:0.5rem';

    const primaryBtn = document.createElement('button');
    primaryBtn.type = 'button';
    primaryBtn.classList.add('viewer-ui-tap-target');
    primaryBtn.style.cssText =
      'width:100%;padding:0.75rem 1rem;background:#a855f7;color:#fff;border:0;border-radius:0.75rem;font-size:0.875rem;font-weight:600;cursor:pointer;transition:background 100ms';
    primaryBtn.textContent = 'Copia link e chiudi';
    primaryBtn.addEventListener('mouseenter', () => {
      primaryBtn.style.background = '#9333ea';
    });
    primaryBtn.addEventListener('mouseleave', () => {
      primaryBtn.style.background = '#a855f7';
    });
    buttons.appendChild(primaryBtn);

    const secondaryBtn = document.createElement('button');
    secondaryBtn.type = 'button';
    secondaryBtn.classList.add('viewer-ui-tap-target');
    secondaryBtn.style.cssText =
      'width:100%;padding:0.75rem 1rem;background:transparent;color:#d4d4d8;border:1px solid rgba(255,255,255,0.20);border-radius:0.75rem;font-size:0.875rem;font-weight:500;cursor:pointer;transition:background 100ms';
    secondaryBtn.textContent = 'Continua comunque';
    secondaryBtn.addEventListener('mouseenter', () => {
      secondaryBtn.style.background = 'rgba(255,255,255,0.05)';
    });
    secondaryBtn.addEventListener('mouseleave', () => {
      secondaryBtn.style.background = 'transparent';
    });
    buttons.appendChild(secondaryBtn);
    dialog.appendChild(buttons);

    // === Lifecycle ===
    let resolved = false;
    const finish = (proceed: boolean): void => {
      if (resolved) return;
      resolved = true;
      document.removeEventListener('keydown', onKeyDown, true);
      wrap.remove();
      // Restore focus to opener if still in the DOM and focusable.
      if (opener !== null && document.contains(opener)) {
        try {
          opener.focus();
        } catch {
          // ignore
        }
      }
      resolve(proceed);
    };

    primaryBtn.addEventListener('click', () => {
      // Codex code review iter 1 fix: disable both buttons immediately
      // when the copy flow starts. Without this, a fast click on
      // "Continua" during the 1200ms toast window would race-win and
      // resolve(true) — overriding the user's explicit "Copia link" choice.
      if (resolved) return;
      primaryBtn.disabled = true;
      secondaryBtn.disabled = true;
      // Visual hint for the disabled state (CSS doesn't auto-grey
      // the inline-styled buttons).
      primaryBtn.style.opacity = '0.7';
      secondaryBtn.style.opacity = '0.5';
      void (async () => {
        try {
          await copyText(props.shareUrl);
          toast.textContent = 'Link copiato. Aprilo da PC desktop.';
          toast.style.opacity = '1';
        } catch {
          toast.textContent = 'Copia non riuscita. Copia manualmente il link dalla barra URL.';
          toast.style.opacity = '1';
        }
        // Brief delay so screen readers announce + user sees confirmation,
        // then close. 1200ms is enough for both without blocking too long.
        setTimeout(() => finish(false), 1200);
      })();
    });

    secondaryBtn.addEventListener('click', () => {
      if (resolved) return;
      finish(true);
    });

    // Focus trap: Tab cycles between primary and secondary.
    const onKeyDown = (ev: KeyboardEvent): void => {
      if (ev.key === 'Escape') {
        ev.preventDefault();
        ev.stopPropagation();
        // ESC = "Copy link" semantic (safe default per UX in plan).
        // We don't actually copy on ESC because user didn't explicitly
        // ask; just resolve false (decline).
        finish(false);
        return;
      }
      if (ev.key === 'Tab') {
        // Focus trap between primaryBtn and secondaryBtn (the only two
        // focusables inside the modal). The list is fixed at construction
        // time so non-null at indices 0 and 1 is guaranteed; use explicit
        // refs (no array indexing) to avoid noNonNullAssertion lint.
        const active = document.activeElement;
        if (ev.shiftKey && active === primaryBtn) {
          ev.preventDefault();
          secondaryBtn.focus();
        } else if (!ev.shiftKey && active === secondaryBtn) {
          ev.preventDefault();
          primaryBtn.focus();
        }
      }
    };
    // capture=true so ESC is caught before any other listener (orchestrator
    // global chain is not yet installed in fetching-meta state, but be
    // defensive in case future code does).
    document.addEventListener('keydown', onKeyDown, true);

    root.appendChild(wrap);
    // Initial focus on primary (safer choice — if user "presses Enter
    // without reading" they get the safe outcome).
    primaryBtn.focus();
  });
}
